import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Chip, Divider, Grid,
  Stepper, Step, StepLabel, StepContent,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { fetchData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const REASONS = ["Relocation", "Financial", "Medical", "Personal", "School Transfer", "Other"];

const WITHDRAWAL_STAGES = [
  { label: "Application Submitted", role: "Parent", description: "Your withdrawal request has been received." },
  { label: "Academic Coordinator Review", role: "Academic Coordinator", description: "The academic team is reviewing the request." },
  { label: "Principal Approval", role: "Principal", description: "Pending principal's approval." },
  { label: "Finance Clearance", role: "Finance", description: "Verifying dues and clearances." },
  { label: "Withdrawal Completed", role: "Admin", description: "Withdrawal process is complete." },
];

const STATUS_CONFIG = {
  Submitted:        { color: "#2196f3", bg: "#e3f2fd", stageIndex: 0 },
  "Under Review":   { color: "#ff9800", bg: "#fff3e0", stageIndex: 1 },
  "Principal Review": { color: "#ff9800", bg: "#fff3e0", stageIndex: 2 },
  "Finance Review": { color: "#ff9800", bg: "#fff3e0", stageIndex: 3 },
  Approved:         { color: "#4caf50", bg: "#e8f5e9", stageIndex: 4 },
  Completed:        { color: "#4caf50", bg: "#e8f5e9", stageIndex: 4 },
  Rejected:         { color: "#f44336", bg: "#fdecea", stageIndex: -1 },
  Pending:          { color: "#4A7BA8", bg: "#EDE9FB", stageIndex: 0 },
};

const EMPTY_FORM = {
  reason: "",
  description: "",
  effectiveDate: "",
};

const ParentWithdrawal = () => {
  const [withdrawalData, setWithdrawalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadWithdrawal = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData("parent/withdrawal");
      if (res?.data) {
        setWithdrawalData(res.data);
      } else {
        setWithdrawalData(null);
      }
    } catch (_) {
      setWithdrawalData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWithdrawal(); }, [loadWithdrawal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.reason) errors.reason = "Please select a reason";
    if (!formData.description.trim()) errors.description = "Description is required";
    if (formData.description.trim().length < 20)
      errors.description = "Please provide a detailed description (at least 20 characters)";
    if (!formData.effectiveDate) errors.effectiveDate = "Effective date is required";
    else {
      const selected = new Date(formData.effectiveDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selected <= today) errors.effectiveDate = "Effective date must be a future date";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await postData("parent/withdrawal", formData);
      const newWithdrawal = res?.data || {
        withdrawalId: `WD${Date.now().toString().slice(-6)}`,
        reason: formData.reason,
        description: formData.description,
        effectiveDate: formData.effectiveDate,
        status: "Submitted",
        submittedDate: new Date().toLocaleDateString("en-GB"),
      };
      setWithdrawalData(newWithdrawal);
      setSubmitted(true);
      setToast({ open: true, severity: "success", message: "Withdrawal request submitted successfully." });
    } catch (_) {
      // Optimistic — show timeline anyway
      const fallbackRecord = {
        withdrawalId: `WD${Date.now().toString().slice(-6)}`,
        reason: formData.reason,
        description: formData.description,
        effectiveDate: formData.effectiveDate,
        status: "Submitted",
        submittedDate: new Date().toLocaleDateString("en-GB"),
      };
      setWithdrawalData(fallbackRecord);
      setSubmitted(true);
      setToast({ open: true, severity: "warning", message: "Request saved locally. Will sync when online." });
    } finally {
      setSubmitting(false);
    }
  };

  const getActiveStep = (status) => {
    const cfg = STATUS_CONFIG[status];
    return cfg ? cfg.stageIndex : 0;
  };

  const renderTimeline = () => {
    if (!withdrawalData) return null;
    const status = withdrawalData.status || "Submitted";
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG["Submitted"];
    const activeStep = getActiveStep(status);
    const isRejected = status === "Rejected";
    const isCompleted = status === "Approved" || status === "Completed";

    return (
      <Box>
        {/* Status banner */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            mb: 3,
          }}
        >
          <Box sx={{ background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", px: 3, py: 2 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "#fff" }}>
              Withdrawal Request Status
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <Typography sx={{ fontSize: "15px", fontWeight: "bold", color: "#111827" }}>
                    Withdrawal ID:
                  </Typography>
                  <Typography sx={{ fontSize: "15px", fontFamily: "monospace", color: "#244C77" }}>
                    {withdrawalData.withdrawalId || "—"}
                  </Typography>
                  <Chip
                    label={status}
                    size="small"
                    sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: "12px" }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                    <strong style={{ color: "#374151" }}>Reason: </strong>{withdrawalData.reason || "—"}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                    <strong style={{ color: "#374151" }}>Effective Date: </strong>
                    {withdrawalData.effectiveDate
                      ? new Date(withdrawalData.effectiveDate).toLocaleDateString("en-GB")
                      : "—"}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                    <strong style={{ color: "#374151" }}>Submitted: </strong>{withdrawalData.submittedDate || "—"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    backgroundColor: cfg.bg,
                    border: `1px solid ${cfg.color}30`,
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "12px", color: cfg.color, fontWeight: 600, mb: 0.5 }}>
                    Current Stage
                  </Typography>
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold", color: cfg.color }}>
                    {isRejected ? "Request Rejected" : isCompleted ? "Process Complete" : WITHDRAWAL_STAGES[Math.max(0, activeStep)]?.label || status}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {withdrawalData.description && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: "#F9FAFB", borderRadius: "10px", border: "1px solid #E5E7EB" }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#374151", mb: 0.5 }}>Description</Typography>
                <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>{withdrawalData.description}</Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Stage Timeline */}
        {!isRejected && (
          <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
            <Box sx={{ background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", px: 3, py: 2 }}>
              <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "#fff" }}>
                Approval Timeline
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {WITHDRAWAL_STAGES.map((stage, idx) => {
                  const isStepDone = idx < activeStep;
                  const isStepActive = idx === activeStep;

                  return (
                    <Step key={stage.label} completed={isStepDone}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: isStepDone
                                ? "#e8f5e9"
                                : isStepActive
                                ? "#EDE9FB"
                                : "#F3F4F6",
                              border: isStepDone
                                ? "2px solid #4caf50"
                                : isStepActive
                                ? "2px solid #244C77"
                                : "2px solid #E5E7EB",
                            }}
                          >
                            {isStepDone ? (
                              <CheckCircleIcon sx={{ fontSize: "18px", color: "#4caf50" }} />
                            ) : isStepActive ? (
                              <PendingIcon sx={{ fontSize: "18px", color: "#244C77" }} />
                            ) : (
                              <AccessTimeIcon sx={{ fontSize: "18px", color: "#D1D5DB" }} />
                            )}
                          </Box>
                        )}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: isStepActive ? 700 : isStepDone ? 600 : 400,
                              color: isStepDone ? "#4caf50" : isStepActive ? "#244C77" : "#9CA3AF",
                            }}
                          >
                            {stage.label}
                          </Typography>
                          <Chip
                            label={stage.role}
                            size="small"
                            sx={{
                              fontSize: "10px",
                              height: 20,
                              backgroundColor: isStepDone ? "#e8f5e9" : isStepActive ? "#EDE9FB" : "#F3F4F6",
                              color: isStepDone ? "#4caf50" : isStepActive ? "#244C77" : "#9CA3AF",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </StepLabel>
                      <StepContent>
                        <Typography sx={{ fontSize: "12.5px", color: "#6B7280", pb: 1.5, ml: 0.5 }}>
                          {stage.description}
                        </Typography>
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
          </Paper>
        )}

        {/* Rejected state */}
        {isRejected && (
          <Paper elevation={0} sx={{ borderRadius: 3, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", p: 3 }}>
            <Box
              sx={{
                backgroundColor: "#fdecea",
                border: "1px solid #ffcdd2",
                borderRadius: "12px",
                p: 2.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <WarningAmberIcon sx={{ color: "#f44336", mt: 0.2 }} />
              <Box>
                <Typography sx={{ fontSize: "14px", fontWeight: "bold", color: "#b71c1c", mb: 0.5 }}>
                  Withdrawal Request Rejected
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#c62828" }}>
                  {withdrawalData.rejectionReason || "Your withdrawal request was not approved. Please contact the school office for more details."}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  const renderForm = () => (
    <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
      {/* Header */}
      <Box sx={{ background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ExitToAppIcon sx={{ color: "#fff", fontSize: "24px" }} />
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "17px", color: "#fff" }}>
              Withdrawal Enquiry
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", mt: 0.2 }}>
              Submit a request to withdraw your child from the school
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Info notice */}
      <Box
        sx={{
          mx: 3,
          mt: 3,
          p: 2,
          backgroundColor: "#FFF3E0",
          borderRadius: "10px",
          border: "1px solid #FFE0B2",
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <WarningAmberIcon sx={{ color: "#E65100", fontSize: "18px", mt: 0.1 }} />
        <Typography sx={{ fontSize: "12.5px", color: "#E65100" }}>
          Please note: Submitting a withdrawal request initiates a formal process. Ensure all dues are cleared before the effective date. This action cannot be undone.
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Reason */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" error={!!formErrors.reason}>
              <InputLabel sx={{ fontSize: "13px" }}>Reason for Withdrawal *</InputLabel>
              <Select
                name="reason"
                value={formData.reason}
                label="Reason for Withdrawal *"
                onChange={handleChange}
                sx={{ fontSize: "13px", borderRadius: "10px" }}
              >
                {REASONS.map((r) => (
                  <MenuItem key={r} value={r} sx={{ fontSize: "13px" }}>{r}</MenuItem>
                ))}
              </Select>
              {formErrors.reason && (
                <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5, ml: 1.5 }}>{formErrors.reason}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Effective Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Effective Date *"
              name="effectiveDate"
              type="date"
              value={formData.effectiveDate}
              onChange={handleChange}
              error={!!formErrors.effectiveDate}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date(Date.now() + 86400000).toISOString().split("T")[0] }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  fontSize: "13px",
                  "& fieldset": { borderColor: "#D5D7DA" },
                  "&:hover fieldset": { borderColor: "#4A7BA8" },
                  "&.Mui-focused fieldset": { borderColor: "#244C77" },
                },
                "& .MuiInputLabel-root": { fontSize: "13px" },
              }}
            />
            {formErrors.effectiveDate && (
              <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5, ml: 1.5 }}>{formErrors.effectiveDate}</Typography>
            )}
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Description / Additional Details *"
              name="description"
              value={formData.description}
              onChange={handleChange}
              error={!!formErrors.description}
              multiline
              rows={4}
              inputProps={{ maxLength: 1000 }}
              placeholder="Please provide details about your reason for withdrawal, including any relevant circumstances..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  fontSize: "13px",
                  "& fieldset": { borderColor: "#D5D7DA" },
                  "&:hover fieldset": { borderColor: "#4A7BA8" },
                  "&.Mui-focused fieldset": { borderColor: "#244C77" },
                },
                "& .MuiInputLabel-root": { fontSize: "13px" },
              }}
            />
            {formErrors.description && (
              <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5, ml: 1.5 }}>{formErrors.description}</Typography>
            )}
            <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.5, textAlign: "right" }}>
              {formData.description.length}/1000
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            onClick={() => setFormData(EMPTY_FORM)}
            disabled={submitting}
            sx={{
              border: "1px solid #D5D7DA",
              borderRadius: "8px",
              color: "#374151",
              textTransform: "none",
              fontSize: "13px",
              px: 2.5,
            }}
          >
            Clear
          </Button>
          <Button
            startIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              background: "linear-gradient(90deg, #4A7BA8, #244C77)",
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "13px",
              px: 3,
              "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
              "&.Mui-disabled": { background: "#E5E7EB", color: "#9CA3AF" },
            }}
          >
            {submitting ? "Submitting..." : "Submit Withdrawal Request"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  const hasActiveWithdrawal = !!(withdrawalData || submitted);

  return (
    <Box sx={{ textAlign: "left", padding: "1rem", fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 1.5 }}>
        Pages / Withdrawal
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#1a1a2e" }}>
          Withdrawal Enquiry
        </Typography>
        <Typography sx={{ color: "#8F95B2", fontSize: "13px", mt: 0.3 }}>
          {hasActiveWithdrawal
            ? "Track the status of your withdrawal request"
            : "Submit a withdrawal request for your child"}
        </Typography>
      </Box>

      {loading ? (
        <Paper elevation={0} sx={{ borderRadius: 3, p: 4, textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
          <Typography sx={{ color: "#9CA3AF", fontSize: "13px" }}>Loading withdrawal information...</Typography>
        </Paper>
      ) : hasActiveWithdrawal ? (
        renderTimeline()
      ) : (
        renderForm()
      )}
    </Box>
  );
};

export default ParentWithdrawal;
